import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useRef } from "react";
import { Messages } from "primereact/messages";
import { Paginator } from "primereact/paginator";
import { useHistory } from "react-router";
import { Panel } from "primereact/panel";
import { BreadCrumb } from "primereact/breadcrumb";
import * as constants from "../app_utils/constants/Constants";
import * as labels from "../app_utils/constants/Labels";
import useShowModalDialog from "../app_utils/components/ShowModalHook";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import { replaceWithUnderscore } from "../app_utils/utils/Utils";
import { getFilterComponent } from "../app_utils/components/Filters";
import { paginatorTemplate } from "../app_utils/components/PaginatorTemplate";
import { filtersHeadertemplate } from "../app_utils/components/FiltersPanelHeader";
import PaymentGatewayFormDialog from "./PaymentGatewayFormDialog";

const PaymentGatewaysView = () => {
  const [records, setRecords] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTermFilter, setSearchTermFilter] = useState<string | null>(null);
  const [recordStatusFilter, setRecordStatusFilter] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [first, setFirst] = useState<number>(0);
  const [limit, setLimit] = useState<number>(constants.MAXIMUM_RECORDS_PER_PAGE);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const offsetRef = useRef<number>(0);
  const message = useRef<any>();
  const history = useHistory();
  const { openDialog, toggleOpenDialog } = useShowModalDialog();

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      history.push(HOME_ROUTE_PATH);
    },
  };

  const breadcrumbItems = [
    {
      label: `Payment Gateways`,
      icon: PrimeIcons.WALLET,
    },
  ];

  const getQueryParameters = () => {
    let searchParameters: any = { offset: offsetRef.current, limit: limit };
    if (searchTermFilter !== null) searchParameters.searchTerm = searchTermFilter;
    if (recordStatusFilter !== null) searchParameters.recordStatus = recordStatusFilter;
    return searchParameters;
  };

  const fetchRecordsFromServer = () => {
    setIsLoading(true);
    let searchParameters: any = getQueryParameters();
    new BaseApiServiceImpl("/api/v1/payment-gateways")
      .getRequestWithJsonResponse(searchParameters)
      .then(async (response) => {
        setIsLoading(false);
        setRecords(response?.records);
        setTotalItems(response?.totalItems);
      })
      .catch((error) => {
        setIsLoading(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    fetchRecordsFromServer();
  }, []);

  const onSubmitFilter = () => {
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
  };

  const resetFilters = () => {
    setSearchTermFilter("");
    setRecordStatusFilter(null);
    fetchRecordsFromServer();
  };

  const openEditFormDialog = (selectedRecord: any) => {
    setSelectedMember(selectedRecord);
    toggleOpenDialog();
  };

  const openNewFormDialog = () => {
    setSelectedMember(null);
    toggleOpenDialog();
  };

  const rowIndexTemplate = (rowData: any, props: any) => {
    return (
      <React.Fragment>
        <span>{first + (props.rowIndex + 1)}</span>
      </React.Fragment>
    );
  };

  const onPageChange = (e: any) => {
    offsetRef.current = e.page * constants.MAXIMUM_RECORDS_PER_PAGE;
    setFirst(e.first);
    setLimit(constants.MAXIMUM_RECORDS_PER_PAGE);
    fetchRecordsFromServer();
  };

  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="actions">
        <Button
          label={labels.LABEL_EDIT}
          className="p-button-sm p-button-warning p-mr-2"
          onClick={() => {
            openEditFormDialog(rowData);
          }}
        />
        <Button
          icon={PrimeIcons.TRASH}
          className="p-button-sm p-button-danger"
          onClick={() => {
            if (window.confirm("Are you sure you want to delete this payment gateway?")) {
              new BaseApiServiceImpl("/api/v1/payment-gateways/" + rowData.id)
                .deleteRequestWithJsonResponse({
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + UserSessionUtils.getBearerToken(),
                  deviceId: UserSessionUtils.getDeviceId(),
                })
                .then(() => {
                  MessageUtils.showSuccessMessage(message, "Payment gateway deleted successfully");
                  fetchRecordsFromServer();
                })
                .catch((error) => {
                  MessageUtils.showErrorMessage(message, error.message);
                });
            }
          }}
        />
      </div>
    );
  };

  const filterButtonsTemplate = (
    <>
      <div className="col-6  md:col-2 p-fluid" key="filterBtns">
        <Button
          icon={constants.ICON_SEARCH}
          className={constants.CSS_FILTER_SUBMIT_BUTTON}
          onClick={onSubmitFilter}
          loading={isLoading}
        />
        <Button
          icon={constants.ICON_REFRESH}
          className={constants.CSS_FILTER_RESET_BUTTON}
          onClick={resetFilters}
          loading={isLoading}
        />
      </div>
    </>
  );

  const filterDetails = [
    {
      type: "text",
      value: searchTermFilter,
      onChangeFn: setSearchTermFilter,
      id: "searchTermFilter",
      label: labels.LABEL_SEARCH_TERM,
      colWidth: constants.CSS_FILTER_SEARCH_INPUT_DIV,
      onkeydownFn: onSubmitFilter,
    },
  ];

  const dynamicFilterDetails = filterDetails.map((filter: any) => {
    let component = getFilterComponent(filter);
    return (
      <div className={filter.colWidth} key={replaceWithUnderscore(filter.id)}>
        <label htmlFor={filter.id}>{filter.label}</label>
        {component}
      </div>
    );
  });

  return (
    <div className="grid">
      <div className="col-6 flex justify-content-start flex-wrap">
        <BreadCrumb home={breadcrumbHome} model={breadcrumbItems} />
      </div>
      <div className="col-6 flex justify-content-end flex-wrap">
        <Button
          label={"New Payment Gateway"}
          icon={PrimeIcons.PLUS}
          className="p-button-secondary p-button-sm"
          onClick={openNewFormDialog}
        />
      </div>
      <Messages ref={message} style={{ width: "100%" }} />
      <div className="col-12">
        <Panel headerTemplate={filtersHeadertemplate} toggleable>
          <div className="grid">
            {dynamicFilterDetails}
            {filterButtonsTemplate}
          </div>
        </Panel>
      </div>
      <div className="col-12 card">
        <DataTable
          value={records}
          paginator={false}
          className="datatable-responsive"
          paginatorPosition="both"
          emptyMessage="No payment gateways found."
          loading={isLoading}
        >
          <Column
            field="Index"
            header="#"
            style={{ width: "70px" }}
            body={rowIndexTemplate}
          ></Column>
          <Column field="name" header="Name" sortable />
          <Column field="providerName" header="Provider" sortable />
          <Column field="productType" header="Product Type" sortable />
          <Column field="baseUrl" header="Base URL" sortable />
          <Column field="merchantId" header="Merchant ID" sortable />
          <Column
            header="Status"
            body={(rowData: any) => (
              <span
                className={`status-badge status-${rowData?.status?.toLowerCase()}`}
              >
                {rowData?.status}
              </span>
            )}
            sortable
          />
          <Column style={{ width: "180px" }} header="Actions" body={actionBodyTemplate}></Column>
        </DataTable>

        <Paginator
          first={first}
          rows={constants.MAXIMUM_RECORDS_PER_PAGE}
          totalRecords={totalItems}
          alwaysShow={true}
          onPageChange={onPageChange}
          template={paginatorTemplate}
        />
      </div>
      {openDialog && (
        <PaymentGatewayFormDialog
          isOpen={openDialog}
          toggle={toggleOpenDialog}
          messageRef={message}
          record={selectedMember}
          reloadFn={fetchRecordsFromServer}
        />
      )}
    </div>
  );
};

export default PaymentGatewaysView;
